import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  AbstractControl,
  ReactiveFormsModule
} from '@angular/forms';
import { I18nPluralPipe } from '@angular/common';
import {
  Configuration,
  LaunchDarklyService,
  AmbassadorState,
  StatusFilter,
  searchParamsFromSellSort,
  ResetFilters,
  SetStatusFilter,
  SearchAuctionItems,
  SellSearchState,
  SellConfigurationState,
  UpdateFilterStatus,
  bsoDealer, ConfigurationState
} from 'remarketing-angular-library';
import { get, template } from 'lodash';
import { Subject } from 'rxjs';
import { Store } from '@ngxs/store';
import MicroModal from 'micromodal';
import { filter, map, takeUntil } from 'rxjs/operators';
import { LeadSellerGroupService } from '../../services/lead-seller-group/lead-seller-group.service';
import { selectedGroupMemberUuids } from '../../services/lead-seller-group/reducer';
import { AnalyticsService } from 'smartauction-frontend-micro-library';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

enum ActionSelection {
  Vin = 'VIN',
  Stock = 'STOCK'
}

@Component({
    selector: 'lib-multi-vin-modal',
    templateUrl: './multi-vin-modal.component.html',
    styleUrls: ['./multi-vin-modal.component.scss'],
    providers: [I18nPluralPipe],
    imports: [TranslocoDirective, ReactiveFormsModule]
})
export class MultiVinModalComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject();

  @Output() toggleViewState = new EventEmitter<string>();

  readonly modalId = 'multi-vin-modal';
  multiVinForm: UntypedFormGroup;
  configuration: Configuration;
  maxLength: number;
  textVin: string;
  textStockNumber: string;

  ActionSelection = ActionSelection;
  dialogHeadlineMsg: string;

  get dialogHeadline(): string {
    return this.dialogHeadlineMsg;
  }

  get entriesList(): string[] {
    return this._transformToEntries(this.controlEntries.value);
  }

  get entriesErrorCount(): string {
    const { difference } = this.controlEntries.errors;
    const message = this._i18nPlural.transform(difference, {
      '=1': '# <%= action %>',
      other: '# <%= action %>s'
    });

    return template(message)({ action: this.labelAction });
  }
  get hasEntriesError(): boolean {
    return !!this.controlEntries.errors;
  }

  get isActionStock(): boolean {
    return this.controlAction.value === ActionSelection.Stock;
  }
  get isActionVin(): boolean {
    return this.controlAction.value === ActionSelection.Vin;
  }

  get labelAction(): string {
    if (this.isActionStock) return this.textStockNumber;
    else if (this.isActionVin) return this.textVin;
  }

  get controlAction(): AbstractControl {
    return this.multiVinForm.get('action');
  }
  get controlEntries(): AbstractControl {
    return this.multiVinForm.get('entries');
  }

  get inServiceVehiclesEnabled() {
    return this._launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  constructor(
    formBuilder: UntypedFormBuilder,
    private _store: Store,
    private _i18nPlural: I18nPluralPipe,
    private readonly _leadSellerGroupService: LeadSellerGroupService,
    private readonly _analyticsService: AnalyticsService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly translocoService: TranslocoService
  ) {
    this.multiVinForm = formBuilder.group({
      action: [ActionSelection.Vin],
      entries: ['', ValidateLength.bind(this)]
    });

    this.controlAction.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this.clearInput());

    function ValidateLength({ value }: AbstractControl) {
      const entries = this._transformToEntries(value);

      if (entries.length > 500) {
        return { difference: entries.length - 500 };
      }
      return null;
    }
  }

  ngOnInit() {
    this.getTranslations();
    this.maxLength = 9498;

    this._store
      .select(state => state.configuration.configuration)
      .pipe(takeUntil(this._destroy$))
      .subscribe(configuration => (this.configuration = configuration));

    this._store
      .select(SellSearchState.stockList)
      .pipe(
        takeUntil(this._destroy$),
        filter(() => this.isActionStock),
        map(stocks => stocks.toString())
      )
      .subscribe(stocks => this.controlEntries.setValue(stocks));

    this._store
      .select(SellSearchState.vinList)
      .pipe(
        takeUntil(this._destroy$),
        filter(() => this.isActionVin),
        map(vins => vins.toString())
      )
      .subscribe(vins => this.controlEntries.setValue(vins));
  }

  closeMicroModal() {
    MicroModal.close(this.modalId);
  }

  onInput() {
    this.controlEntries.setValue(this.controlEntries.value.replace(/[^a-zA-Z0-9\s\,\r\n|\r|\n]/gi, ''));
  }

  onSubmit() {
    const count = this.entriesList.length;
    if (count === 0 || count > 500) return;

    const currentState = this._leadSellerGroupService.snapShot();
    const existingSearchRequest = { ...this._store.selectSnapshot(SellSearchState.searchRequest) };

    // If PCS feature is enabled, default search to all vehicles
    const sellUpdateInventory = this._launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
    let sort = {};

    const isAdmin = this._store.selectSnapshot(ConfigurationState.isAdmin());

    // Separate behavior for Admin users to support streamlined use cases.
    if (sellUpdateInventory) {
      existingSearchRequest.filterByStatus = null;
      this._store.dispatch(new SetStatusFilter(StatusFilter.ALL_VEHICLES));
      this._store.dispatch(new UpdateFilterStatus(StatusFilter.ALL_VEHICLES));
      sort = searchParamsFromSellSort(this._store.selectSnapshot(SellConfigurationState.allSortConfig));
    }
    this._store.dispatch([
      new ResetFilters(),
      new SearchAuctionItems({
        ...sort,
        page: 0,
        filterByStatus: existingSearchRequest.filterByStatus,
        filterByInService: existingSearchRequest.filterByInService,
        tenantUuid: isAdmin ? existingSearchRequest.tenantUuid : null,
        stockList: this.isActionStock ? this.entriesList : [],
        vinList: this.isActionVin ? this.entriesList : [],
        dealerUuid: this._store.selectSnapshot(AmbassadorState.selectedAmbassadorDealershipUuid),
        leadSellerGroupMembers: isAdmin ? null : selectedGroupMemberUuids(currentState),
        grounded: existingSearchRequest.grounded
      })
    ]);

    this.toggleViewState.emit('results');
    this._analyticsService.track('customEvent', {
      attr: {
        formValues: {
          name: 'Multi Vin Search',
          status: 'success',
          errors: null,
          type: '',
          step: '',
          stepname: '',
          items: [
            {
              allow: 'yes',
              error: null,
              name: 'Entries',
              track: 'yes',
              value: this.entriesList
            }
          ]
        }
      }
    });
    this.closeMicroModal();
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  clearInput() {
    this.controlEntries.setValue('');
  }

  stockMvaReference() {
    return this.inServiceVehiclesEnabled ? 'MVA' : 'Stock/Unit #';
  }

  private _transformToEntries(value = ''): string[] {
    return value
      .replace(/[\r\n|\r|\n]+/gi, ',')
      .replace(/[\s,]+/gi, ',')
      .split(',')
      .filter(entry => entry.length);
  }

  getTranslations() {
    this.translocoService.selectTranslateObject('sellModule.manageInventory.multiVinModal').subscribe(translate => {
      this.dialogHeadlineMsg = translate.dialogHeadlineMsg;
      this.textVin = translate.textVIN;
      this.textStockNumber = translate.textStockUnit;
    });
  }
}
